import React from "react";
import { IUser } from "@/services/mocks/userData";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Edit, Mail, Trash2, User } from "lucide-react";
import { getRoleDisplayName } from "@/lib/authUtils";

/**
 * Props del componente UserManagementTable
 */
interface UserManagementTableProps {
  users: IUser[];
  onEditUser: (user: IUser) => void;
  onDeleteUser: (user: IUser) => void;
  isLoading?: boolean;
}

/**
 * Obtiene el color del badge según el rol
 *
 * @param role - Rol del usuario
 * @returns Variante del badge
 */
const getRoleBadgeVariant = (
  role: string
): "default" | "secondary" | "destructive" | "outline" => {
  switch (role) {
    case "Admin":
      return "destructive";
    case "Cajero":
      return "default";
    case "Apoderado":
      return "secondary";
    default:
      return "outline";
  }
};

/**
 * Renderiza los badges de roles con límite de visualización
 *
 * @param roles - Array de roles del usuario
 * @returns JSX con badges de roles
 */
const renderRoleBadges = (roles: string[]) => {
  const maxVisible = 2;
  const visibleRoles = roles.slice(0, maxVisible);
  const hiddenCount = roles.length - maxVisible;

  return (
    <div className="flex flex-wrap gap-1">
      {visibleRoles.map((role) => (
        <Badge
          key={role}
          variant={getRoleBadgeVariant(role)}
          className="text-xs"
        >
          {getRoleDisplayName(role)}
        </Badge>
      ))}
      {hiddenCount > 0 && (
        <Badge variant="outline" className="text-xs">
          +{hiddenCount}
        </Badge>
      )}
    </div>
  );
};

/**
 * Formatea el RUT para mejor visualización
 *
 * @param rut - RUT sin formato
 * @returns RUT formateado
 */
const formatRut = (rut: string): string => {
  // Si ya tiene formato, retornarlo
  if (rut.includes(".")) {
    return rut;
  }

  // Formatear RUT simple (ej: "1-9" -> "1-9")
  return rut;
};

/**
 * Componente de tabla para gestión de usuarios
 *
 * Muestra una tabla completa con información de usuarios,
 * roles con badges de colores y acciones disponibles.
 * En dispositivos móviles se convierte en cards responsive.
 */
const UserManagementTable: React.FC<UserManagementTableProps> = ({
  users,
  onEditUser,
  onDeleteUser,
  isLoading = false,
}) => {
  if (isLoading) {
    return (
      <div className="space-y-4">
        {[...Array(5)].map((_, index) => (
          <Card key={index} className="p-4">
            <div className="flex items-center space-x-4">
              <div className="h-12 w-12 bg-muted rounded-full animate-pulse" />
              <div className="space-y-2 flex-1">
                <div className="h-4 bg-muted rounded animate-pulse" />
                <div className="h-3 bg-muted rounded w-1/2 animate-pulse" />
              </div>
              <div className="h-10 w-24 bg-muted rounded animate-pulse" />
            </div>
          </Card>
        ))}
      </div>
    );
  }

  if (users.length === 0) {
    return (
      <div className="text-center py-12">
        <User className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
        <p className="text-muted-foreground">No se encontraron usuarios</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Vista de Tabla - Desktop */}
      <div className="hidden lg:block rounded-md border border-border">
        <Table>
          <TableHeader>
            <TableRow key="users-header-row">
              <TableHead className="w-[250px] text-foreground">
                Nombre Completo
              </TableHead>
              <TableHead className="w-[120px] text-foreground">RUT</TableHead>
              <TableHead className="w-[200px] text-foreground">Email</TableHead>
              <TableHead className="w-[200px] text-foreground">Roles</TableHead>
              <TableHead className="w-[100px] text-right text-foreground">
                Acciones
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user.id} className="hover:bg-muted/50">
                <TableCell className="font-medium">
                  <div>
                    <div className="font-semibold text-foreground">
                      {user.nombre} {user.apellido}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      ID: {user.id.slice(0, 8)}...
                    </div>
                  </div>
                </TableCell>

                <TableCell>
                  <code className="text-sm bg-muted px-2 py-1 rounded text-foreground">
                    {formatRut(user.rut)}
                  </code>
                </TableCell>

                <TableCell>
                  <div className="flex items-center space-x-2">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <a
                      href={`mailto:${user.email}`}
                      className="text-primary hover:underline text-sm"
                    >
                      {user.email}
                    </a>
                  </div>
                </TableCell>

                <TableCell>{renderRoleBadges(user.roles)}</TableCell>

                <TableCell className="text-right">
                  <div className="flex items-center space-x-2 justify-end">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onEditUser(user)}
                      className="flex items-center space-x-1 h-8 px-3"
                    >
                      <Edit className="h-3 w-3" />
                      <span>Editar</span>
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => onDeleteUser(user)}
                      className="flex items-center space-x-1 h-8 px-3"
                    >
                      <Trash2 className="h-3 w-3" />
                      <span>Eliminar</span>
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Vista de Cards - Mobile/Tablet */}
      <div className="lg:hidden space-y-4">
        {users.map((user) => (
          <Card key={user.id} className="p-4 hover:shadow-md transition-shadow">
            <CardContent className="p-0 space-y-4">
              {/* Header con nombre y acciones */}
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="font-semibold text-lg text-foreground">
                    {user.nombre} {user.apellido}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    ID: {user.id.slice(0, 8)}...
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onEditUser(user)}
                    className="h-9 w-9 p-0"
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => onDeleteUser(user)}
                    className="h-9 w-9 p-0"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Información del usuario */}
              <div className="space-y-3">
                {/* RUT */}
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium text-muted-foreground">
                    RUT:
                  </span>
                  <code className="text-sm bg-muted px-2 py-1 rounded text-foreground">
                    {formatRut(user.rut)}
                  </code>
                </div>

                {/* Email */}
                <div className="flex items-center space-x-2">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <a
                    href={`mailto:${user.email}`}
                    className="text-primary hover:underline text-sm"
                  >
                    {user.email}
                  </a>
                </div>

                {/* Roles */}
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium text-muted-foreground">
                    Roles:
                  </span>
                  <div className="flex-1">{renderRoleBadges(user.roles)}</div>
                </div>
              </div>

              {/* Botones de acción expandidos */}
              <div className="flex items-center space-x-2 pt-2 border-t border-border">
                <Button
                  variant="outline"
                  onClick={() => onEditUser(user)}
                  className="flex-1 h-10"
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Editar Usuario
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => onDeleteUser(user)}
                  className="flex-1 h-10"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Eliminar
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default UserManagementTable;
